import React, { useState, useCallback } from 'react'
import {
  List, Avatar, Button, Input, Upload, Space, Typography, Empty, Spin, Popconfirm,
} from 'antd'
import {
  SendOutlined, PaperClipOutlined, DeleteOutlined, UserOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import useComments from '../../../hooks/useComments'
import { BACKEND_URL } from '../../../services/backendURL'
import { BRAND, BRAND_BG } from '../constants'

const { TextArea } = Input
const { Text } = Typography

const avatarStyle = {
  color: BRAND,
  backgroundColor: BRAND_BG,
  border: `1px solid ${BRAND}`,
}

export default function Comments({ membertaskId, disabled }) {
  const { comments, loading, submitting, addComment, deleteComment } = useComments(membertaskId)
  const [text, setText] = useState('')
  const [fileList, setFileList] = useState([])

  const handleSubmit = useCallback(async () => {
    if (!text.trim() && fileList.length === 0) return
    const files = fileList.map((f) => f.originFileObj || f)
    await addComment(text, files)
    setText('')
    setFileList([])
  }, [text, fileList, addComment])

  const handleFileChange = useCallback(({ fileList: newFileList }) => {
    setFileList(newFileList.slice(-5))
  }, [])

  const getDocUrl = (doc) => {
    if (!doc) return '#'
    const url = doc.doc_file || doc.file || doc.url
    if (!url) return '#'
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`
  }

  return (
    <div>
      {!disabled && (
        <div style={{ marginBottom: 24 }}>
          <TextArea
            rows={3}
            placeholder="Shkruani nje koment..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label="Koment i ri"
          />
          <Space style={{ marginTop: 8 }}>
            <Upload
              fileList={fileList}
              onChange={handleFileChange}
              beforeUpload={() => false}
              multiple
            >
              <Button icon={<PaperClipOutlined />} size="small">
                Bashkangjit
              </Button>
            </Upload>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={submitting}
              onClick={handleSubmit}
              disabled={!text.trim() && fileList.length === 0}
              style={{ backgroundColor: BRAND, borderColor: BRAND }}
              aria-label="Dergo komentin"
            >
              Dergo
            </Button>
          </Space>
        </div>
      )}

      <Spin spinning={loading}>
        {comments.length === 0 && !loading ? (
          <Empty description="Nuk ka komente" />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={comments}
            renderItem={(item) => (
              <List.Item
                actions={
                  !disabled && item.is_mine
                    ? [
                        <Popconfirm
                          key="delete"
                          title="Fshi komentin?"
                          onConfirm={() => deleteComment(item.id)}
                          okText="Po"
                          cancelText="Jo"
                        >
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            aria-label="Fshi komentin"
                          />
                        </Popconfirm>,
                      ]
                    : undefined
                }
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      icon={<UserOutlined />}
                      src={item.member?.photo ? `${BACKEND_URL}${item.member.photo}` : undefined}
                      style={!item.member?.photo ? avatarStyle : undefined}
                      alt={`${item.member?.name || ''} ${item.member?.surname || ''}`}
                    />
                  }
                  title={
                    <Space>
                      <Text strong>
                        {item.member ? `${item.member.name} ${item.member.surname}` : 'Anonim'}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {item.create_date ? moment(item.create_date).format('DD/MM/YYYY HH:mm') : ''}
                      </Text>
                    </Space>
                  }
                  description={
                    <div>
                      <div>{item.comment}</div>
                      {item.docs && item.docs.length > 0 && (
                        <Space wrap style={{ marginTop: 4 }}>
                          {item.docs.map((doc, i) => (
                            <a
                              key={i}
                              href={getDocUrl(doc)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <PaperClipOutlined /> {doc.name || `Dokument ${i + 1}`}
                            </a>
                          ))}
                        </Space>
                      )}
                      {item.comment_response && (
                        <div style={{
                          marginTop: 8,
                          padding: '8px 12px',
                          background: BRAND_BG,
                          borderLeft: `3px solid ${BRAND}`,
                          borderRadius: 4,
                        }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Pergjigje:
                          </Text>
                          <div>{item.comment_response}</div>
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Spin>
    </div>
  )
}
